
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Order {
  id: string;
  status: string;
  total_price: number;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  payment_method: string;
  created_at: string;
  user_id?: string;
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  notes: string;
  product: {
    name: string;
    description: string;
    image_url: string;
  };
}

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);

  const fetchOrders = async () => {
    console.log("Buscando pedidos...");
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Erro ao buscar pedidos:", error);
        throw error;
      }

      console.log("Pedidos encontrados:", data?.length || 0);
      setOrders(data || []);
    } catch (error: any) {
      console.error("Erro detalhado:", error);
      toast({
        title: "Erro ao carregar pedidos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (order: Order) => {
    console.log("Buscando detalhes do pedido:", order.id);
    setSelectedOrder(order);

    try {
      const { data, error } = await supabase
        .from("order_items")
        .select(`
          *,
          product:products(name, description, image_url)
        `)
        .eq("order_id", order.id);

      if (error) {
        console.error("Erro ao buscar itens do pedido:", error);
        throw error;
      }

      console.log("Itens do pedido encontrados:", data?.length || 0);
      setOrderItems(data as OrderItem[]);
    } catch (error: any) {
      console.error("Erro detalhado ao buscar itens:", error);
      toast({
        title: "Erro ao carregar detalhes do pedido",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    console.log("Atualizando status do pedido:", orderId, "para:", newStatus);
    try {
      const { error } = await supabase
        .from("orders")
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq("id", orderId);

      if (error) {
        console.error("Erro ao atualizar status:", error);
        throw error;
      }

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );

      if (selectedOrder?.id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }

      toast({
        title: "Status atualizado",
        description: `Pedido atualizado para: ${getStatusLabel(newStatus)}`,
      });
    } catch (error: any) {
      console.error("Erro detalhado ao atualizar:", error);
      toast({
        title: "Erro ao atualizar status",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Recebido";
      case "preparing":
        return "Preparando";
      case "delivering":
        return "Em Entrega";
      case "completed":
        return "Entregue";
      default:
        return status;
    }
  };

  useEffect(() => {
    fetchOrders();
    
    // Configurar listener em tempo real para novos pedidos
    const ordersSubscription = supabase
      .channel('orders-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders'
        },
        (payload) => {
          console.log('Mudança detectada nos pedidos:', payload);
          fetchOrders(); // Recarregar pedidos quando houver mudanças
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ordersSubscription);
    };
  }, []);

  return {
    orders,
    loading,
    selectedOrder,
    orderItems,
    fetchOrderDetails,
    updateOrderStatus,
    refetch: fetchOrders,
  };
};
